<script lang="ts" setup>
import {useModal} from 'vue-final-modal'
import type {Head} from 'zhead'

import type {AsyncDataRequestStatus, NuxtError} from '#app'
import CreateSubscriber from '~/components/modal-windows/CreateSubscriber.vue'
import Disclaimer from '~/components/products/Disclaimer.vue'
import ProductFact from '~/components/products/ProductFact.vue'
import ProductHeader from '~/components/products/ProductHeader.vue'
import ProductResearch from '~/components/products/ProductResearch.vue'
import ProductTitle from '~/components/products/ProductTitle.vue'
import Tabs from '~/components/products/Tabs.vue'
import Attributes from '~/components/products/tabs/Attributes.vue'
import Description from '~/components/products/tabs/Description.vue'
import Reviews from '~/components/products/tabs/Reviews.vue'
import ConsultationsSection from '~/components/sections/ConsultationsSection.vue'
import MindboxRecommendationsSection from '~/components/sections/MindboxRecommendationsSection.vue'
import ProductQuestionsSection from '~/components/sections/ProductQuestionsSection.vue'
import {useSharing} from '~/composables/sharing'
import constants from '~/constants'
import ImageType from '~/enums/image-type'
import MindboxOperation from '~/enums/mindbox-operation'
import Page from '~/enums/page'
import ProductAttribute from '~/enums/product-attribute'
import ProductLabelEnum from '~/enums/product-label'
import ProductSort from '~/enums/product-sort'
import ProductTab from '~/enums/product-tab'
import SubscriptionForm from '~/enums/subscription-form'
import UnitOfMeasurement from '~/enums/unit-of-measurement'
import YandexMetrikaGoal from '~/enums/yandex-metrika-goal'
import helpers from '~/helpers'
import JsonApiHelper from '~/helpers/json-api-helper'
import EcommerceApiService from '~/services/ecommerce-api-service'
import MindboxApiService from '~/services/mindbox-api-service'
import YandexMetrikaService from '~/services/yandex-metrika-service'
import type {BreadcrumbsItem} from '~/types/breadcrumbs'
import type {ProductAttributeData, ProductData} from '~/types/products'

const { $apiHelper } = useNuxtApp()
const route = useRoute()
const { parsePromoCodeFromQuery } = useSharing()

const allProductAttributes = Object.values(ProductAttribute)
const excludedProductAttributes = [
  ProductAttribute.DescriptionHtml,
  ProductAttribute.Type,
  ProductAttribute.Form,
  ProductAttribute.UnitOfMeasurement,
  ProductAttribute.UnitQuantity
]
const breadcrumbsItems: BreadcrumbsItem[] = [
  { name: 'Главная', path: Page.Main },
  { name: 'Каталог', path: Page.Search }
]
const previewTypes = [ImageType.Thumbnail240p, ImageType.Original]

// Tabs.
const currentTab = ref(ProductTab.Description)

// const activeIngredientsIds = computed(() => productData.value.meta.activeIngredientsIds ?? [])
const attributesData = computed(() => {
  const { heightInMm, ingredients, lengthInMm, weightInGrams, widthInMm } = productData.value.attributes

  const attributes: ProductAttributeData[] = [
    { id: 'ingredients', name: 'Состав', value: ingredients },
    { id: 'lengthInMm', name: 'Длина, мм', value: lengthInMm },
    { id: 'widthInMm', name: 'Ширина, мм', value: widthInMm },
    { id: 'heightInMm', name: 'Высота, мм', value: heightInMm },
    { id: 'weightInGrams', name: 'Вес, г', value: weightInGrams }
  ].filter((x: ProductAttributeData): boolean => x.value !== null)

  const metaAttributes = productData.value.meta.attributes
  const extraAttributes = []

  let unitOfMeasurement = null
  let unitQuantity = null

  for (const { id, name, value } of metaAttributes) {
    if (allProductAttributes.includes(id as ProductAttribute)) {
      if (!excludedProductAttributes.includes(id as ProductAttribute)) {
        extraAttributes.push({
          id: 'extraAttribute',
          name,
          value: ProductAttribute.formatValue(id as ProductAttribute, value) as string
        })
      }

      if (id === ProductAttribute.UnitOfMeasurement) {
        unitOfMeasurement = value
      } else if (id === ProductAttribute.UnitQuantity) {
        unitQuantity = value
      }
    }
  }

  if (unitOfMeasurement !== null && unitQuantity !== null) {
    attributes.push({
      id: 'unitOfMeasurement',
      name: UnitOfMeasurement.getLabelText(unitOfMeasurement as UnitOfMeasurement),
      value: unitQuantity
    })
  }

  if (extraAttributes.length) {
    attributes.push(...extraAttributes)
  }

  return attributes
})
// const bodySystemsIds = computed(() => productData.value.meta.bodySystemsIds ?? [])
const brandData = computed(() => productData.value.relationships.brand?.data)
const categoriesData = computed(() => productData.value.relationships.categories?.data ?? [])
const id = computed(() => productData.value.id)
const isConsultation = computed(
  () => productData.value.relationships.categories.data.some(
    (x: any): boolean => x.id === constants.categories.consultations.id
  )
)
const isMarathon = computed(() => [3936, 3937].includes(Number(id.value)))
const isPurchasedByUser = computed(() => productData.value.meta.isPurchasedByUser)
const name = computed(() => productData.value.attributes.name)
const price = computed(() => helpers.getProductPrice(productData.value))
const productId = computed(() => helpers.getIdFromSlugAndId(route.params.slugAndId as string))
const seoData = computed(() => productData.value.attributes.seoData)
const seoH1 = computed(() => seoData.value?.h1 ?? name.value)
const tagsData = computed(
  () => (productData.value.relationships.tags?.data ?? [])
    .filter((x: any): boolean => !ProductLabelEnum.hasDescription(x.attributes.name))
)
const userReviewId = computed(() => productData.value.meta.userReviewId)

const actualFullPath = ref<string | null>(null)

const [
  { data: productResponse, error: productError, status: productStatus },
  { data: consultationsResponse, status: consultationsStatus }
] = await Promise.all([
  useLazyAsyncData(
    async () => {
      try {
        const response = await $apiHelper.products.getProduct(productId.value as string)

        const { fullPath, path } = route
        const actualPath = helpers.getProductPath(response.data)

        if (path !== actualPath) {
          actualFullPath.value = fullPath.replace(path, actualPath)

          if (import.meta.client) {
            history.replaceState({}, '', actualFullPath.value)
          }
        }

        return response
      } catch (error) {
        showError(error as NuxtError)
      }
    },
    {
      transform: x => JsonApiHelper.denormalizeResponse(x)
    }
  ),
  useLazyAsyncData(
    () => {
      const consultationsParams = {
        'filter[categoryId]': constants.categories.consultations.id,
        'page[size]': 12,
        sort: ProductSort.Random
      }

      return $apiHelper.products.getProducts(consultationsParams)
    },
    {
      transform: x => JsonApiHelper.denormalizeResponse(x)
    }
  )
])

if (productError.value) {
  throw createError(productError.value as NuxtError)
}

if (actualFullPath.value) {
  await navigateTo(actualFullPath.value, { redirectCode: 301 })
}

const productData = computed<ProductData>(() => productResponse.value?.data)
const consultationsData = computed<ProductData[]>(() => consultationsResponse.value?.data ?? [])

watch(productStatus, async (x: AsyncDataRequestStatus): Promise<void> => {
  if (x === 'success') { // After preloading.
    await trackPageView()
  }
})

useHead(() => {
  if (productStatus.value !== 'success') {
    return {}
  }

  const previewUrl = getPreviewUrl()

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: productData.value.attributes.name,
    offers: { '@type': 'Offer', price: price.value, priceCurrency: 'RUB' }
  }

  if (brandData.value) {
    (jsonLd as any).brand = { '@type': 'Brand', name: brandData.value.attributes.name }
  }

  if (productData.value.attributes.descriptionHtml) {
    (jsonLd as any).description = productData.value.attributes.descriptionHtml
  }

  if (previewUrl) {
    (jsonLd as any).image = previewUrl
  }

  const head: Head = {
    title: seoData.value?.title ?? `Купить ${seoH1.value} по цене ${price.value} ₽ в интернет-магазине iTAB | наличие, стоимость, доставка, отзывы, заказ на сайте`,
    meta: [
      {
        name: 'description',
        content: seoData.value?.description
          ?? `Купить ${seoH1.value} в Москве и с доставкой во все регионы России и по миру. Ознакомиться с каталогом и ценами и заказать онлайн в интернет-магазине iTAB. Описание продукта, состав, инструкция по применению, отзывы покупателей. Широкий ассортимент, известные производители, скидки и акции.`
      },
      { property: 'og:description', content: '' },
      { name: 'og:title', property: 'og:title', content: `${seoH1.value} — iTAB` }
    ],
    script: [
      {
        innerHTML: JSON.stringify(jsonLd),
        type: 'application/ld+json'
      }
    ]
  }

  if (head.meta && previewUrl) {
    head.meta.push(...helpers.getPreviewMetadata(previewUrl))
  }

  return head
})

onMounted(async (): Promise<void> => {
  YandexMetrikaService.sendMetricEvent(YandexMetrikaGoal.ViewProduct)

  await parsePromoCodeFromQuery()

  const query = { ...route.query }

  if (productStatus.value === 'success') { // After SSR.
    const { productIds, utmCampaign, utmMedium, utmSource } = constants.utmParameters.maamCpcMarket

    if (
      query.utm_source === utmSource
      && query.utm_medium === utmMedium
      && query.utm_campaign === utmCampaign
      && productIds.includes(id.value)
    ) {
      const delayBeforeShowingInSeconds = 10

      setTimeout(openCreateSubscriberModalWindow, 1000 * delayBeforeShowingInSeconds)
    }

    await trackPageView()
  }

  // if (query.utm_campaign === 'imuali-super-osteo-complex') {
  //   setTimeout(
  //     (): void => {
  //       const element = document.querySelector('a[href^="https://drive.google.com/"]')
  //
  //       if (element) {
  //         const offsetTop = element.getBoundingClientRect().top + window.scrollY
  //         const offset = 60
  //
  //         window.scrollTo({ top: offsetTop - offset, behavior: 'smooth' })
  //       }
  //     },
  //     1000
  //   )
  // }
})

function getPreviewUrl (): string | null {
  const imageUrls = helpers.getImageUrls(productData.value.relationships.images.data)

  return imageUrls ? helpers.getImageUrl(imageUrls, previewTypes) : null
}

async function openCreateSubscriberModalWindow (): Promise<void> {
  await useModal({ component: CreateSubscriber, attrs: { subscriptionForm: SubscriptionForm.Guide } }).open()
}

function setTotalPublishedReviews (x: number): void {
  productData.value.attributes.totalPublishedReviews = x
}

function setUserReviewId (x: string | null): void {
  productData.value.meta.userReviewId = x
}

async function trackPageView (): Promise<void> {
  EcommerceApiService.viewItem(
    productData.value.id,
    productData.value.attributes.name,
    price.value,
    brandData.value ? brandData.value.attributes.name : null,
    categoriesData.value.map((x: any): string => x.attributes.name).sort()
  )

  MindboxApiService.viewProduct(productData.value.id)
}
</script>

<template>
  <div>
    <Preloader v-if="productStatus === 'pending'" />
    <template v-else-if="productStatus === 'success' && productData">
      <section class="mb-10">
        <div class="container">
          <Breadcrumbs :current="seoH1" :items="breadcrumbsItems" />
        </div>
      </section>

      <section class="mb-6">
        <ProductHeader :product-data="productData" />
      </section>

      <MindboxRecommendationsSection
        v-if="!isMarathon"
        class="mb-10"
        :data="{ recommendation: { limit: 12, product: { ids: { itab: id } } } }"
        :operation="
          productData.meta?.isOutOfStock
            ? MindboxOperation.SimilarProductsAmongOwnBrads
            : MindboxOperation.SimilarProducts
        "
        title="Похожие товары"
      />

      <Tabs
        v-model="currentTab"
        class="mb-6"
        :product-data="productData"
        :total-attributes="attributesData.length"
        :total-published-reviews="productData.attributes.totalPublishedReviews"
      />

      <section class="mb-10">
        <div class="container">
          <div class="content-wrapper">
            <Description v-show="currentTab === ProductTab.Description" :product-data="productData" :tags-data="tagsData" />

            <Attributes v-show="currentTab === ProductTab.Attributes" :attributes-data="attributesData" />

            <Reviews
              v-show="currentTab === ProductTab.Reviews"
              :is-product-purchased-by-user="isPurchasedByUser"
              :product-id="id"
              :user-review-id="userReviewId"
              @set-total-published-reviews="setTotalPublishedReviews"
              @set-user-review-id="setUserReviewId"
            />

            <ProductQuestionsSection
              v-show="currentTab === ProductTab.Questions"
              :product-id="productId"
            />
          </div>
        </div>
      </section>

      <section v-if="productData.relationships.facts?.data.length" class="mb-10">
        <div class="container">
          <ProductTitle class="mb-6" title="Интересные факты" />

          <div v-for="x in productData.relationships.facts.data" :key="x.id" class="content-wrapper mb-6">
            <ProductFact :product-fact-data="x" />
          </div>
        </div>
      </section>

      <section v-if="productData.relationships.researches?.data.length" class="mb-10">
        <div class="container">
          <ProductTitle class="mb-6" title="Исследования" />

          <div v-for="x in productData.relationships.researches.data" :key="x.id" class="content-wrapper mb-6">
            <ProductResearch :product-research-data="x" />
          </div>
        </div>
      </section>

      <MindboxRecommendationsSection
        v-if="!isMarathon"
        class="mb-10"
        :data="{ recommendation: { limit: 12, product: { ids: { itab: id } } } }"
        :operation="MindboxOperation.RelatedProducts"
        :title="'С\xA0этим товаром покупают'"
      />

      <ConsultationsSection v-if="consultationsStatus === 'success' && !isConsultation && !isMarathon" class="mb-10" />

      <section>
        <div class="container">
          <Disclaimer class="content-wrapper" />
        </div>
      </section>
    </template>
  </div>
</template>
